export const KriteriaType = {
  NARASI: 'narasi',
  TABEL: 'tabel',
};

export const getBabNumber = (kodeKriteria) => {
  const value = String(kodeKriteria || '').match(/\d+/)?.[0];
  return value ? Number(value) : '';
};

export const formatKriteriaLabel = (criteria) =>
  `${criteria.kode_kriteria} - ${criteria.nama_kriteria}`;

export const normalizeKriteriaCode = (value) =>
  String(value || '').trim().toUpperCase();

export const normalizeFieldName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const normalizeFieldType = (value) => {
  const type = String(value || '').toLowerCase();
  if (!type || type === 'textarea') return 'text';
  return ['text', 'number', 'select'].includes(type) ? type : 'text';
};

export const parseFieldOptions = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  if (typeof value === 'string') {
    return value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => ({ value: item, label: item }));
  }

  return [];
};

export const mapCriteriaForAccess = (criteriaList) =>
  criteriaList.map((criteria) => ({
    ...criteria,
    code: criteria.kode_kriteria,
    name: criteria.nama_kriteria,
    menu: criteria.kode_kriteria.toLowerCase(),
    label: formatKriteriaLabel(criteria),
    route: `/kriteria/${criteria.kode_kriteria.toLowerCase()}`,
  }));

const mapProdiAssignmentToCriteria = (assignment) => {
  const baseCriteria = assignment.kriteria || {};
  const fields = (assignment.fields || [])
    .filter((field) => field.is_active)
    .map((field) => ({
      ...field,
      tipe_field: normalizeFieldType(field.tipe_field),
    }))
    .sort((a, b) => (a.urutan || 0) - (b.urutan || 0));

  return {
    ...baseCriteria,
    id: baseCriteria.id || assignment.kriteria_id,
    kode_kriteria: normalizeKriteriaCode(baseCriteria.kode_kriteria),
    nama_kriteria: assignment.nama_kriteria_override || baseCriteria.nama_kriteria,
    tipe_kriteria: assignment.tipe_kriteria_override || KriteriaType.NARASI,
    is_active: Boolean(baseCriteria.is_active && assignment.is_active),
    fields,
    prodiAssignment: assignment,
  };
};

export const fetchKriteriaListForProdi = async (
  supabase,
  prodiId,
  { includeInactive = false } = {}
) => {
  if (!prodiId) return [];

  let query = supabase
    .schema('akreditasi')
    .from('kriteria_prodi')
    .select(`
      id,
      kriteria_id,
      prodi_id,
      nama_kriteria_override,
      tipe_kriteria_override,
      is_active,
      kriteria:kriteria_id (
        id,
        kode_kriteria,
        nama_kriteria,
        deskripsi,
        urutan,
        is_active,
        created_at,
        updated_at
      ),
      fields:kriteria_prodi_fields (
        id,
        nama_field,
        label,
        tipe_field,
        options,
        urutan,
        required,
        is_active
      )
    `)
    .eq('prodi_id', prodiId);

  if (!includeInactive) {
    query = query.eq('is_active', true);
  }

  const result = await query;

  if (result.error) {
    throw new Error(`Gagal mengambil kriteria prodi: ${result.error.message}`);
  }

  return (result.data || [])
    .filter((assignment) => includeInactive || assignment.kriteria?.is_active)
    .map(mapProdiAssignmentToCriteria)
    .sort((a, b) => (a.urutan || 0) - (b.urutan || 0));
};

export const fetchKriteriaGroupsForProdiList = async (
  supabase,
  prodiList,
  { includeInactive = false } = {}
) => {
  const prodiIds = (prodiList || []).map((prodi) => prodi.id).filter(Boolean);
  if (prodiIds.length === 0) return [];

  let query = supabase
    .schema('akreditasi')
    .from('kriteria_prodi')
    .select(`
      id,
      kriteria_id,
      prodi_id,
      nama_kriteria_override,
      tipe_kriteria_override,
      is_active,
      kriteria:kriteria_id (
        id,
        kode_kriteria,
        nama_kriteria,
        deskripsi,
        urutan,
        is_active,
        created_at,
        updated_at
      ),
      fields:kriteria_prodi_fields (
        id,
        nama_field,
        label,
        tipe_field,
        options,
        urutan,
        required,
        is_active
      )
    `)
    .in('prodi_id', prodiIds);

  if (!includeInactive) {
    query = query.eq('is_active', true);
  }

  const result = await query;

  if (result.error) {
    throw new Error(`Gagal mengambil grup kriteria prodi: ${result.error.message}`);
  }

  const assignmentsByProdi = (result.data || []).reduce((groups, assignment) => {
    if (!includeInactive && !assignment.kriteria?.is_active) return groups;
    const criteria = mapProdiAssignmentToCriteria(assignment);
    if (!groups[assignment.prodi_id]) groups[assignment.prodi_id] = [];
    groups[assignment.prodi_id].push(criteria);
    return groups;
  }, {});

  return (prodiList || []).map((prodi) => ({
    prodi,
    criteria: (assignmentsByProdi[prodi.id] || []).sort(
      (a, b) => (a.urutan || 0) - (b.urutan || 0)
    ),
  }));
};

export const fetchKriteriaList = async (
  supabase,
  { includeInactive = false, prodiId = null } = {}
) => {
  if (prodiId) {
    return fetchKriteriaListForProdi(supabase, prodiId, { includeInactive });
  }

  let query = supabase
    .schema('akreditasi')
    .from('kriteria')
    .select(`
      *,
      fields:kriteria_fields (
        id,
        nama_field,
        label,
        tipe_field,
        options,
        urutan,
        required,
        is_active
      )
    `)
    .order('urutan', { ascending: true });

  if (!includeInactive) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(`Gagal mengambil daftar kriteria: ${error.message}`);
  }

  const criteriaList = (data || []).map((criteria) => ({
    ...criteria,
    kode_kriteria: normalizeKriteriaCode(criteria.kode_kriteria),
    fields: (criteria.fields || [])
      .filter((field) => includeInactive || field.is_active)
      .map((field) => ({
        ...field,
        tipe_field: normalizeFieldType(field.tipe_field),
      }))
      .sort((a, b) => (a.urutan || 0) - (b.urutan || 0)),
  }));

  return criteriaList;
};

export const fetchKriteriaByCode = async (supabase, kodeKriteria, { prodiId = null } = {}) => {
  const code = normalizeKriteriaCode(kodeKriteria);

  if (prodiId) {
    const criteriaForProdi = await fetchKriteriaListForProdi(supabase, prodiId);
    return criteriaForProdi.find((criteria) => criteria.kode_kriteria === code) || null;
  }

  const { data, error } = await supabase
    .schema('akreditasi')
    .from('kriteria')
    .select(`
      *,
      fields:kriteria_fields (
        id,
        nama_field,
        label,
        tipe_field,
        options,
        urutan,
        required,
        is_active
      )
    `)
    .eq('kode_kriteria', code)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    throw new Error(`Gagal mengambil kriteria ${code}: ${error.message}`);
  }

  if (!data) return null;

  const criteria = {
    ...data,
    kode_kriteria: normalizeKriteriaCode(data.kode_kriteria),
    fields: (data.fields || [])
      .filter((field) => field.is_active)
      .map((field) => ({
        ...field,
        tipe_field: normalizeFieldType(field.tipe_field),
      }))
      .sort((a, b) => (a.urutan || 0) - (b.urutan || 0)),
  };

  return criteria;
};
